/**
 * Realtor CRM hooks (React Query) — contacts, threads, timelines, enquiries,
 * schedule.
 *
 * Queries unwrap the service's `ApiResult` and throw its message, so pages get
 * React Query's error state and can show `error.message`. Mutations do the
 * same, and invalidate every cache the write can change.
 */

import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
	type ApiResult,
	changeLeadStage,
	createActivity,
	listMySchedule,
	updateLead,
} from "../services/leads-api.service";
import {
	type ChatImportInput,
	type ContactInput,
	type ContactMessageInput,
	type ContactType,
	type ConvertEnquiryInput,
	clearFollowUpSnooze,
	commitChatImport,
	convertEnquiry,
	createContact,
	createEnquiry,
	deleteContact,
	dismissEnquiry,
	dismissListingMatch,
	type EnquiryInput,
	type EnquiryStatus,
	type FollowUpList,
	getContact,
	getContactTimeline,
	getEnquiry,
	listContacts,
	listEnquiries,
	listFollowUps,
	listThreads,
	logContactMessage,
	logEnquiryMessage,
	type MessageInput,
	previewChatImport,
	snoozeFollowUp,
	updateContact,
} from "../services/realtor-crm-api.service";

async function unwrap<T>(pending: Promise<ApiResult<T>>): Promise<T> {
	const result = await pending;
	if (!result.success) throw new Error(result.error ?? "Something went wrong");
	return result.data as T;
}

// Contacts --------------------------------------------------------------------

export function useContacts(filter: { q?: string; type?: ContactType }) {
	return useQuery({
		queryKey: queryKeys.realtor.contacts(filter),
		queryFn: () => unwrap(listContacts(filter)),
		// Keep the list on screen while a new search loads, so typing doesn't
		// flash an empty state between keystrokes.
		placeholderData: keepPreviousData,
	});
}

export function useContact(id: string | null) {
	return useQuery({
		queryKey: queryKeys.realtor.contact(id ?? ""),
		queryFn: () => unwrap(getContact(id as string)),
		enabled: Boolean(id),
	});
}

function useInvalidateContacts() {
	const queryClient = useQueryClient();
	return () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: queryKeys.realtor.contacts() }),
			queryClient.invalidateQueries({ queryKey: queryKeys.realtor.threads() }),
		]);
}

export function useCreateContact() {
	const invalidate = useInvalidateContacts();
	return useMutation({
		mutationFn: (input: ContactInput) => unwrap(createContact(input)),
		onSuccess: invalidate,
	});
}

export function useUpdateContact() {
	const invalidate = useInvalidateContacts();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (args: { id: string; input: Partial<ContactInput> }) =>
			unwrap(updateContact(args.id, args.input)),
		onSuccess: async (_data, args) => {
			await invalidate();
			await queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.contact(args.id),
			});
		},
	});
}

export function useDeleteContact() {
	const invalidate = useInvalidateContacts();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => unwrap(deleteContact(id)),
		onSuccess: async () => {
			await invalidate();
			// Its leads lose their contact link.
			await queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.leads(),
			});
		},
	});
}

// Messages --------------------------------------------------------------------

export function useThreads() {
	return useQuery({
		queryKey: queryKeys.realtor.threads(),
		queryFn: () => unwrap(listThreads()),
	});
}

/**
 * A person's timeline, newest page first. `data.pages[0]` is the newest page
 * and each later page is older; entries within a page are oldest first.
 */
export function useContactTimeline(contactId: string | null) {
	return useInfiniteQuery({
		queryKey: queryKeys.realtor.timeline(contactId ?? ""),
		queryFn: ({ pageParam }) =>
			unwrap(getContactTimeline(contactId as string, pageParam)),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => lastPage.nextBefore ?? undefined,
		enabled: Boolean(contactId),
	});
}

/**
 * Everything a logged conversation or a changed lead activity can move: the
 * person's timeline, the Messages inbox, Home's schedule and the follow-ups.
 */
export function useInvalidateConversation() {
	const queryClient = useQueryClient();
	return (contactId: string) =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.timeline(contactId),
			}),
			queryClient.invalidateQueries({ queryKey: queryKeys.realtor.threads() }),
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.schedule(),
			}),
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.followUps(),
			}),
		]);
}

export function useLogContactMessage() {
	const invalidate = useInvalidateConversation();
	return useMutation({
		mutationFn: (args: { contactId: string; input: ContactMessageInput }) =>
			unwrap(logContactMessage(args.contactId, args.input)),
		onSuccess: (_data, args) => invalidate(args.contactId),
	});
}

// Chat import -----------------------------------------------------------------

/** Reads an uploaded chat export. A mutation: it posts a file each time. */
export function usePreviewChatImport() {
	return useMutation({
		mutationFn: (args: { file: File; contactId?: string }) =>
			unwrap(previewChatImport(args.file, { contactId: args.contactId })),
	});
}

/**
 * Saves an import. It can create a contact and a lead and fills a timeline,
 * so the contacts book, the inbox, every timeline, the leads and the
 * follow-ups all go stale.
 */
export function useCommitChatImport() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (args: { file: File; input: ChatImportInput }) =>
			unwrap(commitChatImport(args.file, args.input)),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.threads(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.contacts(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.timelines(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.followUps(),
				}),
				queryClient.invalidateQueries({ queryKey: queryKeys.realtor.leads() }),
			]),
	});
}

// Enquiries -------------------------------------------------------------------

export function useEnquiries(filter: { status?: EnquiryStatus }) {
	return useQuery({
		queryKey: queryKeys.realtor.enquiries(filter),
		queryFn: () => unwrap(listEnquiries(filter)),
	});
}

export function useEnquiry(id: string | null) {
	return useQuery({
		queryKey: queryKeys.realtor.enquiry(id ?? ""),
		queryFn: () => unwrap(getEnquiry(id as string)),
		enabled: Boolean(id),
	});
}

function useInvalidateEnquiries() {
	const queryClient = useQueryClient();
	return (id?: string) =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.enquiries(),
			}),
			...(id
				? [
						queryClient.invalidateQueries({
							queryKey: queryKeys.realtor.enquiry(id),
						}),
					]
				: []),
			// An enquiry from a known number also lands in that contact's thread
			// and timeline.
			queryClient.invalidateQueries({ queryKey: queryKeys.realtor.threads() }),
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.timelines(),
			}),
		]);
}

export function useCreateEnquiry() {
	const invalidate = useInvalidateEnquiries();
	return useMutation({
		mutationFn: (input: EnquiryInput) => unwrap(createEnquiry(input)),
		onSuccess: () => invalidate(),
	});
}

export function useLogEnquiryMessage() {
	const invalidate = useInvalidateEnquiries();
	return useMutation({
		mutationFn: (args: { enquiryId: string; input: MessageInput }) =>
			unwrap(logEnquiryMessage(args.enquiryId, args.input)),
		onSuccess: (_data, args) => invalidate(args.enquiryId),
	});
}

export function useDismissEnquiry() {
	const invalidate = useInvalidateEnquiries();
	return useMutation({
		mutationFn: (id: string) => unwrap(dismissEnquiry(id)),
		onSuccess: (_data, id) => invalidate(id),
	});
}

export function useConvertEnquiry() {
	const invalidate = useInvalidateEnquiries();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (args: { id: string; input: ConvertEnquiryInput }) =>
			unwrap(convertEnquiry(args.id, args.input)),
		onSuccess: async (_data, args) => {
			await invalidate(args.id);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: queryKeys.realtor.leads() }),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.contacts(),
				}),
			]);
		},
	});
}

// Follow-ups ------------------------------------------------------------------

/**
 * The realtor's current nudges and snoozed people. Computed on read by the
 * server, so it is fetched again whenever the window regains focus: the
 * realtor is often coming back from WhatsApp or the dialler.
 */
const followUpListQuery = {
	queryKey: queryKeys.realtor.followUpList(),
	queryFn: () => unwrap(listFollowUps()),
	staleTime: 0,
	refetchOnWindowFocus: true,
};

export function useFollowUps() {
	return useQuery(followUpListQuery);
}

export function useSnoozeFollowUp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (args: { contactId: string; until: string }) =>
			unwrap(snoozeFollowUp(args.contactId, args.until)),
		onSuccess: async (_data, args) => {
			// Move the person to the snoozed row now rather than after the
			// refetch, so the card leaves as soon as the server has accepted.
			queryClient.setQueryData<FollowUpList>(
				queryKeys.realtor.followUpList(),
				(current) => {
					if (!current) return current;
					const entry = current.followUps.find(
						(f) => f.contact.id === args.contactId,
					);
					if (!entry) return current;
					return {
						followUps: current.followUps.filter((f) => f !== entry),
						snoozed: [
							...current.snoozed,
							{ contact: entry.contact, until: args.until },
						],
					};
				},
			);
			await queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.followUps(),
			});
		},
	});
}

export function useClearFollowUpSnooze() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (contactId: string) => unwrap(clearFollowUpSnooze(contactId)),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.followUps(),
			}),
	});
}

/** "Not a fit" on a listing match. The person's nudge may change or go. */
export function useDismissListingMatch() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (args: { leadId: string; listingId: string }) =>
			unwrap(dismissListingMatch(args.leadId, args.listingId)),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.realtor.followUps(),
			}),
	});
}

/**
 * Creates the "follow up next" reminder on a lead. A reminder moves the
 * schedule, the lead lists, the person's timeline and the follow-ups.
 */
export function useCreateFollowUpReminder() {
	const queryClient = useQueryClient();
	const invalidateConversation = useInvalidateConversation();
	return useMutation({
		mutationFn: (args: {
			contactId: string | null;
			leadId: string;
			title: string;
			scheduledAt: string;
		}) =>
			unwrap(
				createActivity(args.leadId, {
					type: "reminder",
					title: args.title,
					scheduledAt: args.scheduledAt,
				}),
			),
		onSuccess: async (_data, args) => {
			await Promise.all([
				args.contactId
					? invalidateConversation(args.contactId)
					: queryClient.invalidateQueries({
							queryKey: queryKeys.realtor.schedule(),
						}),
				queryClient.invalidateQueries({ queryKey: queryKeys.realtor.leads() }),
			]);
		},
	});
}

export type LeadMark = "cold" | "lost";

export interface MarkLeadsResult {
	succeeded: string[];
	failed: string[];
	/** The first failure's message; null when every lead was marked. */
	error: string | null;
}

/**
 * Marks each lead cold (temperature) or lost (stage `closed_lost`) through
 * the existing lead endpoints, one at a time. Reports which leads failed
 * instead of throwing, so the sheet can keep just those selected.
 */
export function useMarkLeads() {
	const queryClient = useQueryClient();
	const invalidateConversation = useInvalidateConversation();
	return useMutation({
		mutationFn: async (args: {
			contactId: string;
			leadIds: string[];
			mark: LeadMark;
		}): Promise<MarkLeadsResult> => {
			const result: MarkLeadsResult = {
				succeeded: [],
				failed: [],
				error: null,
			};
			for (const leadId of args.leadIds) {
				const outcome =
					args.mark === "cold"
						? await updateLead(leadId, { temperature: "cold" })
						: await changeLeadStage(leadId, "closed_lost");
				if (outcome.success) {
					result.succeeded.push(leadId);
				} else {
					result.failed.push(leadId);
					result.error ??= outcome.error ?? null;
				}
			}
			return result;
		},
		onSettled: async (_data, _error, args) => {
			await Promise.all([
				invalidateConversation(args.contactId),
				queryClient.invalidateQueries({ queryKey: queryKeys.realtor.leads() }),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.contact(args.contactId),
				}),
			]);
		},
	});
}

// Schedule --------------------------------------------------------------------

/** Open scheduled activities before `to`; omit `from` to include overdue. */
export function useSchedule(range: { from?: Date; to: Date }) {
	return useQuery({
		queryKey: queryKeys.realtor.schedule({
			from: range.from?.toISOString() ?? null,
			to: range.to.toISOString(),
		}),
		queryFn: () => unwrap(listMySchedule(range)),
	});
}
